import Flickity from 'flickity';

/*
* Get Inspired Carousel
* @param {object} opts
*/

function getInspiredCarousel(opts) {
  const config = {
    dom: {
      carousel: 'js-get-inspired-carousel',
      carouselCell: 'js-get-inspired-carousel-cell',
    },
    flickityOpts: {
      desktop: {
        prevNextButtons: true,
        pageDots: true,
        draggable: true,
        wrapAround: true,
        imagesLoaded: true,
        lazyLoad: true,
        adaptiveHeight: false,
        accessibility: false,
      },
      mobile: {
        prevNextButtons: false,
        pageDots: true,
        draggable: true,
        wrapAround: true,
        imagesLoaded: true,
        lazyLoad: true,
        adaptiveHeight: false,
        accessibility: false,
      },
    },
    cls: {
      flickityOff: 'flickity-off',
    },
  };

  const c = __.extend(config, opts);
  const $carousels = document.body.querySelectorAll(`.${c.dom.carousel}`);
  const cellVisibleRatio = 0.5;
  let view = null;

  /**
   * Init
   */
  function init() {
    if (!$carousels?.length) return;
    onWindowResize();
    __.windowResize(onWindowResize);
  }

  /**
  * Responsive functions
  */
  function onWindowResize() {
    __.mq({
      view: 'desktop',
      callback: () => {
        if (view !== 'desktop') {
          view = 'desktop';
          createCarousel(c.flickityOpts.desktop);
        }
      },
    });

    __.mq({
      view: 'mobile',
      callback: () => {
        if (view !== 'mobile') {
          view = 'mobile';
          createCarousel(c.flickityOpts.mobile);
        }
      },
    });
  }

  /**
   * Create Carousel
   * @param {object} opts.flickityOpts
   */
  function createCarousel(flickityOpts) {
    $carousels.forEach(($carousel) => {
      let flickity = null;
      let cellAlign = 0;

      const handleResize = () => {
        const $flickityCell = $carousel.querySelector(`.${c.dom.carouselCell}`);

        const cellsWidth = calculateCellsWidth($carousel);
        const shouldInitialize = cellsWidth > window.innerWidth;

        if (shouldInitialize && !flickity) {
          if (view === 'mobile') {
            // Mobile view: Center the main slide
            cellAlign = 0.5;
          } else {
            // Desktop view: Calculate offset
            const offsetDivisor = 1; // Updated value
            const leftOffset = setFlickityOffset($flickityCell.offsetWidth, offsetDivisor);
            const carouselOffset = $carousel.offsetWidth;
            cellAlign = leftOffset / carouselOffset;
          }

          const extendedFlickityOpts = __.extend(flickityOpts, { cellAlign });
          flickity = new Flickity($carousel, extendedFlickityOpts);
          bindCellAccessibility(flickity);

          // Calculate and apply the maximum height to the cells
          const updateCellHeight = () => {
            let maxHeight = 0;
            const carouselCells = $carousel.querySelectorAll(`.${c.dom.carouselCell}`);
            carouselCells.forEach((cell) => {
              cell.style.height = 'auto'; // Reset the height first
              const cellHeight = cell.offsetHeight;
              if (cellHeight > maxHeight) {
                maxHeight = cellHeight;
              }
            });
            carouselCells.forEach((cell) => {
              cell.style.height = `${maxHeight}px`; // Apply the maximum height
            });
          };

          updateCellHeight(); // Initial calculation

          window.addEventListener('resize', () => {
            if (flickity) {
              flickity.resize();
              syncCellAccessibility(flickity);
            }
            updateCellHeight();
          });

          __.removeClass($carousel, c.cls.flickityOff);
        } else if (!shouldInitialize && flickity) {
          clearCellAccessibility($carousel.querySelectorAll(`.${c.dom.carouselCell}`));
          flickity.destroy();
          flickity = null;
          __.addClass($carousel, c.cls.flickityOff);
        }
      };

      // Add 'flickity-off' class initially
      __.addClass($carousel, c.cls.flickityOff);

      handleResize(); // Initial calculation

      window.addEventListener('resize', handleResize);
    });
  }

  function getCellVisibleRatio($cell, $viewport) {
    const cellRect = $cell.getBoundingClientRect();
    const viewportRect = $viewport.getBoundingClientRect();
    const visibleWidth =
      Math.min(cellRect.right, viewportRect.right) - Math.max(cellRect.left, viewportRect.left);

    return cellRect.width > 0 ? visibleWidth / cellRect.width : 0;
  }

  function getVisualCells(flickity) {
    const $viewport = flickity?.viewport;
    if (!$viewport || !flickity.cells?.length) return [];

    return flickity.cells
      .map((cell) => cell.element)
      .filter(($cell) => getCellVisibleRatio($cell, $viewport) >= cellVisibleRatio)
      .sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);
  }

  function getFocusedCell(flickity) {
    if (!flickity.element.contains(document.activeElement)) return null;

    return document.activeElement.closest(`.${c.dom.carouselCell}`);
  }

  function setCellTabEntry(flickity, $entryCell) {
    flickity.cells.forEach((cell) => {
      cell.element.tabIndex = cell.element === $entryCell ? 0 : -1;
    });
  }

  function syncCellAccessibility(flickity) {
    const $viewport = flickity?.viewport;
    if (!$viewport || !flickity.cells?.length) return;

    const $visualCells = getVisualCells(flickity);
    const $focusedCell = getFocusedCell(flickity);

    flickity.cells.forEach((cell) => {
      const $cell = cell.element;

      if ($visualCells.includes($cell) || $cell === $focusedCell) {
        $cell.removeAttribute('aria-hidden');
        $cell.inert = false;
      } else {
        $cell.setAttribute('aria-hidden', 'true');
        $cell.inert = true;
      }
    });

    setCellTabEntry(flickity, $focusedCell || $visualCells[0]);
  }

  function handleCellTab(e, flickity) {
    if (e.key !== 'Tab') return;

    // lazy loaded images resize the cells, so the state is refreshed before acting on it
    syncCellAccessibility(flickity);

    const $visualCells = getVisualCells(flickity);
    if (!$visualCells.length) return;

    const $currentCell = e.target.closest(`.${c.dom.carouselCell}`);
    const currentIndex = $visualCells.indexOf($currentCell);

    if (currentIndex === -1) {
      const [$prevButton] = flickity.element.querySelectorAll('.flickity-prev-next-button');

      // shift-tabbing back into the carousel lands on the rightmost visible cell
      if (e.shiftKey && e.target === $prevButton) {
        e.preventDefault();
        $visualCells[$visualCells.length - 1].focus({ preventScroll: true });
      }

      return;
    }

    const $nextCell = $visualCells[e.shiftKey ? currentIndex - 1 : currentIndex + 1];
    if (!$nextCell) return;

    e.preventDefault();
    $nextCell.focus({ preventScroll: true });
  }

  function handleCellFocus(e, flickity) {
    const $cell = e.target.closest(`.${c.dom.carouselCell}`);
    if (!$cell) return;

    if (flickity.viewport.scrollLeft !== 0) flickity.viewport.scrollLeft = 0;

    setCellTabEntry(flickity, $cell);
  }

  function bindCellAccessibility(flickity) {
    if (!flickity) return;

    const sync = () => syncCellAccessibility(flickity);

    sync();
    flickity.on('ready', sync);
    flickity.on('select', sync);
    flickity.on('settle', sync);
    flickity.on('lazyLoad', sync);
    flickity.element.addEventListener('focusin', (e) => handleCellFocus(e, flickity));
    flickity.element.addEventListener('keydown', (e) => handleCellTab(e, flickity));
  }

  function clearCellAccessibility(cells) {
    [...cells].forEach(($cell) => {
      $cell.removeAttribute('aria-hidden');
      $cell.removeAttribute('tabindex');
      $cell.inert = false;
    });
  }

  function calculateCellsWidth($carousel) {
    const carouselCells = $carousel.querySelectorAll(`.${c.dom.carouselCell}`);
    let cellsWidth = 0;
    const cellsCount = carouselCells.length;

    carouselCells.forEach((cell, index) => {
      if (index === cellsCount - 1) {
        cellsWidth += cell.offsetWidth / 1.5; // Add half of the width for the last cell
      } else {
        cellsWidth += cell.offsetWidth;
      }
    });

    return cellsWidth;
  }

  /**
   * Set the flickity carousel offset
   */
  function setFlickityOffset(flickityCellOffsetWidth, flickityOffsetDivisor) {
    Flickity.Cell.prototype.setDefaultTarget = () => {
      const marginProperty = this.parent.originSide === 'left' ? 'marginLeft' : 'marginRight';
      this.target = this.x + this.size[marginProperty];
    };

    return flickityCellOffsetWidth / flickityOffsetDivisor;
  }

  return init();
}

export default getInspiredCarousel;
